import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { defaultDeal } from "../lib/format";
import { FileDown, Save, Trash2, BadgeCheck, Layers, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Deal Template Loader — 1-click presets + save-as-template.
 *
 * - Built-in templates ship from the backend.
 * - Users can save the *current* deal as a custom template ("Save current as template").
 * - Applying a template ALWAYS mints a fresh `referenceNumber` (and the
 *   underlying tx — once created via POST /api/transactions — gets a fresh
 *   uuid4 id + fresh binder SHA-256). The template never stores identity.
 */
export default function DealTemplateLoader({ deal, setDeal, mode, setMode }) {
  const [templates, setTemplates] = useState([]);
  const [selected, setSelected] = useState("");
  const [savedRef, setSavedRef] = useState(null);
  const [savingLabel, setSavingLabel] = useState(false);

  const reload = useCallback(() => {
    axios.get(`${API}/deal-templates`)
      .then(r => setTemplates(r.data.templates || []))
      .catch(() => setTemplates([]));
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const apply = async (id) => {
    setSelected(id);
    if (!id) return;
    try {
      // Server-truth apply — mints a fresh reference + closing dates.
      const r = await axios.post(`${API}/deal-templates/${id}/apply`);
      const fresh = r.data.deal || {};
      setDeal({ ...defaultDeal, ...fresh });
      if (setMode && r.data.path) setMode(r.data.path);
      setSavedRef({ kind: "applied", ref: r.data.fresh_reference });
    } catch (e) {
      // Fallback to client-side merge if endpoint somehow fails
      const tpl = templates.find(t => t.id === id);
      if (tpl) {
        setDeal({ ...defaultDeal, ...tpl.overrides });
        if (setMode && tpl.path) setMode(tpl.path);
      }
    }
    setTimeout(() => setSelected(""), 1500);
    setTimeout(() => setSavedRef(null), 5000);
  };

  const saveCurrent = async () => {
    if (!deal) return;
    const label = window.prompt(
      "Save current deal as a re-usable template.\n\nName it (e.g. \"Medcare Q3 receivables\"):",
      `${(deal.borrowerName || deal.trustName || "Custom deal").slice(0, 40)} — $${Number(deal.faceValue || 0).toLocaleString()} / ${deal.termDays || 90}d`
    );
    if (!label) return;
    try {
      setSavingLabel(true);
      const r = await axios.post(`${API}/deal-templates`, {
        label,
        path: mode || "discount_sale",
        overrides: deal,
        reference_prefix: `TH-${(deal.borrowerName || deal.trustName || "DEAL").split(" ")[0].replace(/[^A-Z0-9]/gi, "").toUpperCase().slice(0, 10)}`,
      });
      await reload();
      setSavedRef({ kind: "saved", ref: r.data.label });
      setTimeout(() => setSavedRef(null), 5000);
    } catch (e) {
      alert("Failed to save template: " + (e.response?.data?.detail || e.message));
    } finally {
      setSavingLabel(false);
    }
  };

  const removeTemplate = async (id, label) => {
    if (!window.confirm(`Delete custom template "${label}"?`)) return;
    try {
      await axios.delete(`${API}/deal-templates/${id}`);
      await reload();
    } catch (e) {
      alert("Failed to delete: " + (e.response?.data?.detail || e.message));
    }
  };

  const selectedTpl = templates.find(t => t.id === selected);

  // ---------- Bulk-from-template ----------
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkCount, setBulkCount] = useState(5);
  const [bulkRail, setBulkRail] = useState("direct_exchange");
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);
  const [bulkTplId, setBulkTplId] = useState("");

  // Default the bulk template to the first custom-saved template, or fallback
  useEffect(() => {
    if (!bulkTplId && templates.length) {
      const firstCustom = templates.find(t => t.is_custom);
      setBulkTplId(firstCustom?.id || templates[0]?.id || "");
    }
  }, [templates, bulkTplId]);

  const runBulk = async () => {
    if (!bulkTplId) { alert("Pick a template first."); return; }
    const tpl = templates.find(t => t.id === bulkTplId);
    if (!window.confirm(`Spawn ${bulkCount} copies of "${tpl?.label}" via ${bulkRail.replace(/_/g, " ")}?\n\nEach gets a unique tx_id + binder SHA-256 + reference number.`)) return;
    setBulkLoading(true); setBulkResult(null);
    try {
      const r = await axios.post(`${API}/issuance/bulk-from-template/${bulkTplId}`, {
        count: Number(bulkCount),
        settlement_rail: bulkRail,
      });
      setBulkResult(r.data);
    } catch (e) {
      setBulkResult({ error: e.response?.data?.detail || e.message });
    }
    setBulkLoading(false);
  };

  return (
    <div className="mt-3 pt-3 border-t border-[#E5E5DF]" data-testid="deal-template-loader">
      <div className="text-[9.5px] uppercase tracking-[0.18em] text-[#5C5C5C] mb-1.5 flex items-center justify-between">
        <span className="flex items-center gap-1">
          <FileDown size={10} /> Load a preset template
        </span>
        <button
          type="button"
          onClick={saveCurrent}
          disabled={savingLabel}
          className="flex items-center gap-1 text-[#0B4636] hover:underline disabled:opacity-50"
          data-testid="save-template-btn"
          title="Save current deal as a re-usable template (fresh tx_id + binder hash on every replay)"
        >
          <Save size={10} /> {savingLabel ? "Saving…" : "Save current as template"}
        </button>
      </div>

      <select
        value={selected}
        onChange={(e) => apply(e.target.value)}
        className="w-full text-[12px] border border-[#E5E5DF] rounded-sm px-2 py-1.5 bg-white text-[#0A0A0A] hover:border-[#0B4636] transition-colors"
        data-testid="template-select"
      >
        <option value="">— Pick a template —</option>
        {templates.filter(t => t.is_custom).length > 0 && (
          <optgroup label="Your saved templates">
            {templates.filter(t => t.is_custom).map(t => (
              <option key={t.id} value={t.id}>★ {t.label}</option>
            ))}
          </optgroup>
        )}
        <optgroup label="Built-in templates">
          {templates.filter(t => !t.is_custom).map(t => (
            <option key={t.id} value={t.id}>{t.label}</option>
          ))}
        </optgroup>
      </select>

      {selectedTpl?.is_custom && (
        <button
          type="button"
          onClick={() => removeTemplate(selectedTpl.id, selectedTpl.label)}
          className="mt-1 flex items-center gap-1 text-[10px] text-[#9E2A2B] hover:underline"
          data-testid="delete-template-btn"
        >
          <Trash2 size={10} /> Delete this custom template
        </button>
      )}

      {savedRef && (
        <div
          className="mt-2 text-[10.5px] text-[#0B4636] flex items-start gap-1 bg-[#0B4636]/5 border border-[#0B4636]/20 rounded-sm px-2 py-1.5"
          data-testid="template-feedback"
        >
          <BadgeCheck size={12} className="mt-[1px] flex-shrink-0" />
          <div>
            {savedRef.kind === "saved" && (
              <>
                Saved <strong>{savedRef.ref}</strong> as a re-usable template. Every replay mints a brand-new <code>tx_id</code> + binder SHA-256.
              </>
            )}
            {savedRef.kind === "applied" && (
              <>
                Applied. Fresh reference: <code className="font-mono text-[10px]">{savedRef.ref}</code>. The next saved transaction will get a new tx_id and unique binder hash.
              </>
            )}
          </div>
        </div>
      )}

      {/* ---------- Bulk-from-template ---------- */}
      <button
        type="button"
        onClick={() => setBulkOpen((o) => !o)}
        className="mt-3 w-full flex items-center justify-between text-[10px] uppercase tracking-[0.16em] text-[#1a3a8e] hover:text-[#142c6c] font-bold py-1 px-2 border border-[#1a3a8e]/30 rounded-sm bg-[#1a3a8e]/5"
        data-testid="bulk-from-template-toggle"
      >
        <span className="flex items-center gap-1.5"><Layers size={11} /> Bulk · Spawn N from template</span>
        <span>{bulkOpen ? "−" : "+"}</span>
      </button>

      {bulkOpen && (
        <div className="mt-1.5 p-2 border border-[#1a3a8e]/30 bg-[#1a3a8e]/[0.03] rounded-sm" data-testid="bulk-from-template-panel">
          <div className="text-[10px] text-[#5C5C5C] mb-1.5">
            Spawn multiple closings from one template. Each gets a unique <code>tx_id</code> + binder SHA-256 + reference.
          </div>
          <label className="text-[9.5px] uppercase tracking-[0.12em] text-[#5C5C5C] block mb-0.5">Template</label>
          <select
            value={bulkTplId}
            onChange={(e) => setBulkTplId(e.target.value)}
            className="w-full text-[11px] border border-[#E5E5DF] rounded-sm px-2 py-1 mb-1.5 bg-white"
            data-testid="bulk-template-select"
          >
            <option value="">— Select —</option>
            {templates.map(t => (
              <option key={t.id} value={t.id}>{t.is_custom ? "★ " : ""}{t.label}</option>
            ))}
          </select>

          <div className="grid grid-cols-2 gap-1.5 mb-1.5">
            <div>
              <label className="text-[9.5px] uppercase tracking-[0.12em] text-[#5C5C5C] block mb-0.5">Count</label>
              <input
                type="number" min={1} max={100}
                value={bulkCount}
                onChange={(e) => setBulkCount(e.target.value)}
                className="w-full text-[11px] border border-[#E5E5DF] rounded-sm px-2 py-1 bg-white font-mono"
                data-testid="bulk-count-input"
              />
            </div>
            <div>
              <label className="text-[9.5px] uppercase tracking-[0.12em] text-[#5C5C5C] block mb-0.5">Settlement</label>
              <select
                value={bulkRail}
                onChange={(e) => setBulkRail(e.target.value)}
                className="w-full text-[11px] border border-[#E5E5DF] rounded-sm px-2 py-1 bg-white"
                data-testid="bulk-rail-select"
              >
                <option value="direct_exchange">Direct Exchange (bypass Increase)</option>
                <option value="stripe">Stripe Checkout</option>
                <option value="ach_debit">ACH Debit (NACHA)</option>
                <option value="none">None · just create</option>
              </select>
            </div>
          </div>

          <button
            onClick={runBulk}
            disabled={bulkLoading || !bulkTplId}
            className="w-full bg-[#1a3a8e] hover:bg-[#142c6c] text-white text-[10.5px] uppercase tracking-[0.14em] py-1.5 rounded-sm disabled:opacity-50 flex items-center justify-center gap-1.5"
            data-testid="bulk-run-btn"
          >
            {bulkLoading ? <><Loader2 size={11} className="animate-spin" /> Spawning…</> : <><Layers size={11} /> Spawn {bulkCount} closings</>}
          </button>

          {bulkResult && (
            <div className={`mt-2 p-2 rounded-sm text-[10px] ${bulkResult.error ? "bg-red-50 text-red-800 border border-red-200" : "bg-[#1a3a8e]/10 border border-[#1a3a8e]/30 text-[#1a3a8e]"}`}>
              {bulkResult.error ? (
                <>Error: {bulkResult.error}</>
              ) : (
                <>
                  <div className="font-bold text-[11px]">✓ Batch {bulkResult.batch_id?.slice(0, 8)}… · {bulkResult.count} deals</div>
                  <div className="font-mono-fin mt-0.5">Total face ${Number(bulkResult.total_face).toLocaleString()} · purchase ${Number(bulkResult.total_purchase).toLocaleString()}</div>
                  <div className="mt-1 max-h-32 overflow-y-auto">
                    {bulkResult.created?.slice(0, 10).map((c, i) => (
                      <div key={c.id} className="font-mono text-[9.5px] truncate">{i + 1}. {c.id.slice(0, 8)}… · {c.reference}</div>
                    ))}
                    {bulkResult.created?.length > 10 && <div className="text-[9px] opacity-70">…and {bulkResult.created.length - 10} more</div>}
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
